import { Slot } from '@radix-ui/react-slot';

import { activateOnKey, interactive } from '@/components/ui/interactive';
import { clickedInteractive } from '@/lib/detail';
import { cn } from '@/lib/utils';
import type { ComponentProps, MouseEvent } from 'react';

/**
 * The white panel the whole console is built from: `rounded-2xl`, a concrete hairline
 * border, no shadow at rest. Every dashboard widget, list container and settings block
 * in the prototype is this shape.
 *
 * A `div` by default; `asChild` lends the shell to the element inside instead — a
 * detail screen's `<header>`, a reply `<form>`, a section — so the markup keeps its
 * meaning and the shell is still written once.
 */
export function Card({
  className,
  asChild = false,
  onActivate,
  ...props
}: ComponentProps<'div'> & {
  asChild?: boolean;
  /**
   * Makes the whole card open something — a list card that leads to its record (#145).
   * The card becomes a tab stop that answers Enter and Space, shows the shared focus
   * ring and the pointer, and a click anywhere on it follows, except on its own links
   * and buttons (`clickedInteractive`), which keep their clicks. It is a `group` named
   * by the caller's `aria-label`, not a `button`: a button may not contain the links and
   * copy buttons these cards carry.
   */
  onActivate?: () => void;
}) {
  const Comp = asChild ? Slot : 'div';
  const activation = onActivate
    ? {
        role: 'group',
        tabIndex: 0,
        'data-interactive': 'true',
        onClick: (event: MouseEvent<HTMLDivElement>) => {
          if (!clickedInteractive(event.target)) onActivate();
        },
        onKeyDown: activateOnKey(onActivate),
      }
    : {};
  return (
    <Comp
      data-slot="card"
      className={cn(
        'rounded-2xl border border-conc-100 bg-white',
        onActivate && [interactive, 'hover:border-navy/30 hover:shadow-md'],
        className,
      )}
      {...activation}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: ComponentProps<'div'>) {
  return <div data-slot="card-header" className={cn('px-5 pt-5 pb-4', className)} {...props} />;
}

export function CardTitle({ className, children, ...props }: ComponentProps<'h2'>) {
  return (
    <h2
      data-slot="card-title"
      className={cn('text-[15px] font-bold text-navy', className)}
      {...props}
    >
      {children}
    </h2>
  );
}

export function CardDescription({ className, ...props }: ComponentProps<'p'>) {
  return (
    <p
      data-slot="card-description"
      className={cn('text-[12.5px] text-conc-500', className)}
      {...props}
    />
  );
}

/**
 * `pt-0` when a `CardHeader` precedes it, full padding when it is the whole card. The
 * alternative is every caller remembering which of the two shapes it is using.
 */
export function CardContent({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('p-5 [&:not(:first-child)]:pt-0', className)}
      {...props}
    />
  );
}
